import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";

export default function ReviewQueue() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/review-queue").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (inst) => {
    setBusy(true);
    try {
      await api.post(`/installations/${inst.id}/approve`);
      toast.success(t("review.approvedToast"));
      setActive(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decline = async (inst) => {
    if (!comments.trim()) return toast.error(t("review.commentsRequired"));
    setBusy(true);
    try {
      await api.post(`/installations/${inst.id}/decline`, { comments });
      toast.success(t("review.declinedToast"));
      setActive(null); setComments(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!items)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="review-queue">
      <div>
        <h1 className="font-heading text-3xl font-black text-slate-900">{t("review.title")}</h1>
        <p className="mt-1 text-slate-500">{t("review.awaiting", { count: items.length })}</p>
      </div>

      {items.length === 0 && <p className="text-sm text-slate-400">{t("review.nothing")}</p>}
      <div className="grid gap-3">
        {items.map((inst) => (
          <Card key={inst.id} data-testid={`review-${inst.id}`} className="flex flex-wrap items-center justify-between gap-3 border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-100 text-amber-600"><ClipboardCheck className="h-5 w-5" /></div>
              <div>
                <div className="font-heading font-bold text-slate-900">{inst.bus_number || inst.expected_bus_serial}</div>
                <div className="text-sm text-slate-500">{inst.bus_type || inst.bus_variant} · {t("review.route")} {inst.signoff_route}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={inst.status} />
              <Button data-testid={`open-review-${inst.id}`} onClick={() => setActive(inst)} className="bg-blue-600 hover:bg-blue-500">{t("review.review")}</Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={() => { setActive(null); setComments(""); }}>
        <DialogContent className="max-w-2xl border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("review.proofReview", { bus: active?.bus_number || active?.expected_bus_serial })}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {[["review.before", "photos_before"], ["review.after", "photos_after"]].map(([label, key]) => {
                  const list = active[key] || [];
                  return (
                    <div key={key}>
                      <div className="overline mb-1.5 text-slate-500">{t(label)} ({list.length})</div>
                      <div className="grid grid-cols-2 gap-2">
                        {list.length ? list.map((p) => (
                          <img key={p} data-testid={`review-img-${key}`} src={fileUrl(p)} alt={t(label)} className="w-full rounded-md border border-slate-200" />
                        )) : <div className="col-span-2 flex h-32 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400">{t("review.noPhoto")}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Textarea data-testid="decline-comments" value={comments} onChange={(e) => setComments(e.target.value)} placeholder={t("review.commentsPlaceholder")} className="border-slate-300 bg-white" />
              <div className="flex gap-3">
                <Button data-testid="decline-btn" onClick={() => decline(active)} disabled={busy} variant="outline" className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50">
                  <XCircle className="h-4 w-4" /> {t("review.decline")}
                </Button>
                <Button data-testid="approve-btn" onClick={() => approve(active)} disabled={busy} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-500">
                  <CheckCircle2 className="h-4 w-4" /> {t("review.approve")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
